import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Heart, Clock, Sparkles, Maximize2, X, Music } from "lucide-react";
import { usePlayer } from "./PlayerContext";

const absUrl = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const Chip = ({ t, lane }) => {
  const p = usePlayer();
  const live = p.current?.item_id === t.item_id;
  return (
    <button type="button" onClick={() => (live ? p.toggle() : p.play(t, { keepMinimized: true }))} className={`group shrink-0 w-[108px] text-left rounded-xl p-1.5 transition-colors ${live ? "bg-[#54E3FF]/15 ring-1 ring-[#54E3FF]/60" : "hover:bg-white/10"}`} data-testid={`kwiki-${lane}-${t.item_id}`}>
      <div className="relative h-[96px] w-[96px] rounded-lg overflow-hidden bg-white/10">
        {t.album_art_url ? <img src={absUrl(t.album_art_url)} alt="" className="h-full w-full object-cover" loading="lazy" /> : <Music className="h-8 w-8 text-white/40 absolute inset-0 m-auto" />}
        <span className={`absolute inset-0 flex items-center justify-center bg-black/35 ${live ? "" : "opacity-0 group-hover:opacity-100"} transition-opacity`}>
          <span className="h-9 w-9 rounded-full bg-amber-400 text-amber-950 flex items-center justify-center">{live && p.playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}</span>
        </span>
      </div>
      <p className="m-0 mt-1 text-[11px] font-bold leading-tight truncate text-white">{t.title}</p>
      <p className="m-0 text-[10px] text-white/60 truncate">{t.artist_name}</p>
    </button>
  );
};

const Lane = ({ icon: Icon, title, tracks, lane, empty, action }) => (
  <section data-testid={`kwiki-lane-${lane}`}>
    <div className="flex items-center justify-between px-3">
      <h3 className="m-0 text-[10px] font-black tracking-[0.25em] text-[#54E3FF] inline-flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {title}</h3>
      {action}
    </div>
    {tracks.length ? (
      <div className="mt-1.5 flex gap-1 overflow-x-auto px-2 pb-1 kwiki-scroll">{tracks.map((t) => <Chip key={t.item_id} t={t} lane={lane} />)}</div>
    ) : <p className="m-0 mt-1.5 px-3 pb-1 text-xs text-white/55">{empty}</p>}
  </section>
);

// Kwiki — the collapsed player opens to three lanes (Zune Quickplay): Pinned · Recently played · New on THE WALL.
export const KwikiPanel = ({ onClose, onExpand }) => {
  const p = usePlayer();
  const byId = useMemo(() => Object.fromEntries(p.lib.tracks.map((t) => [t.item_id, t])), [p.lib.tracks]);
  const pinned = useMemo(() => p.lib.playlist.map((id) => byId[id]).filter(Boolean).reverse(), [p.lib.playlist, byId]);
  const recent = useMemo(() => p.recentIds.map((id) => byId[id]).filter(Boolean), [p.recentIds, byId]);
  const fresh = useMemo(() => [...p.lib.tracks].filter((t) => t.added_at).sort((a, b) => (b.added_at > a.added_at ? 1 : -1)).slice(0, 12), [p.lib.tracks]);
  const member = p.lib.access !== "anonymous";
  return (
    <div className="w-[min(92vw,420px)] rounded-3xl bg-[#120A24]/95 backdrop-blur-xl text-white shadow-2xl ring-1 ring-[#54E3FF]/40 pb-2 player-pop" data-testid="kwiki-panel">
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
        <span className="text-[11px] font-black tracking-[0.3em] text-amber-300" data-testid="kwiki-title">KWIKI</span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={onExpand} className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 px-2.5 py-1 text-[11px] font-bold" data-testid="kwiki-full-player"><Maximize2 className="h-3 w-3" /> Full player</button>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Close" data-testid="kwiki-close"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="space-y-2">
        <Lane icon={Heart} title="PINNED" lane="pinned" tracks={pinned}
          empty={member ? "Tap the ♥ on any song to pin it here." : "Pins need a free account — one tap, no email."}
          action={!member && <Link to="/passport" className="text-[10px] font-bold text-amber-300 underline" data-testid="kwiki-pin-cta">Get one →</Link>} />
        <Lane icon={Clock} title="RECENTLY PLAYED" lane="recent" tracks={recent} empty="Your last 20 songs on this device show up here." />
        <Lane icon={Sparkles} title="NEW ON THE WALL" lane="new" tracks={fresh} empty="Fresh tracks land here as artists add them." />
      </div>
    </div>
  );
};
